import { BaseEditViewStore, BaasicDropdownStore } from "core/stores";
import { PatientMissingDocsEditForm } from 'application/patient-missing-docs/forms';
import { FacilityOrderTemplateService } from "application/facility-order/services";
import { DiagnosisCodeService } from "application/import/services";
import { PatientService } from "application/patient/services";
import { LocalDoctorService } from 'application/import/services';
import { action, observable } from "mobx";
import { ModalParams } from "core/models";
import _ from "lodash";
import { inactiveReasons } from 'core/constants';

class PatientMissingDocsTemplateEditViewStore extends BaseEditViewStore {
    @observable accessor facilityOrderTemplate;
    @observable accessor isItemVisible = false;
    @observable accessor disabled = false;
    constructor(rootStore, componentsProps) {
        const facilityOrderTemplateService = new FacilityOrderTemplateService(rootStore.application.baasic.apiClient);
        const patientService = new PatientService(rootStore.application.baasic.apiClient);
        const facilityOrderTemplate = componentsProps.item;
        super(rootStore, {
            name: 'patient-missing-docs-template-edit',
            Form: PatientMissingDocsEditForm,
            id: facilityOrderTemplate.id,
            autoInit: false,
            authorization: 'portalSection.update',
            actions: () => {
                return {
                    get: async () => {
                        return {
                            ...facilityOrderTemplate,
                            facilityOrderTemplateMissingDocuments: facilityOrderTemplate.facilityOrderTemplateMissingDocuments.map(i => ({ ...i.missingDocument, facilityOrderTemplateMissingDocumentId: i.id, note: i.note })),
                            height: facilityOrderTemplate.patient.height,
                            weight: facilityOrderTemplate.patient.weight,
                            includeInDocReqEmail: facilityOrderTemplate.includeInDocReqEmail === true
                        };
                    },
                    update: async (resource) => {
                        resource.facilityOrderTemplateMissingDocuments = resource.facilityOrderTemplateMissingDocuments && resource.facilityOrderTemplateMissingDocuments.map(i => { return { missingDocumentId: i.id, note: i.note } });
                        const response = await facilityOrderTemplateService.updateNewFlowClinicalTemplate({ ...facilityOrderTemplate, ...resource });
                        return response.data;
                    },
                    updatePatientDetails: async (resource) => {
                        const response = await patientService.updatePatientDetails(resource);
                        return response;
                    },
                }
            },
            FormClass: PatientMissingDocsEditForm
        });

        this.rootStore = rootStore;
        this.latestFacilityOrderId = componentsProps.latestFacilityOrderId;
        this.isCurrent = this.latestFacilityOrderId == facilityOrderTemplate.facilityOrderId;
        this.missingDocuments = componentsProps.missingDocuments;
        this.lookups = componentsProps.lookups;
        this.facilityOrderTemplate = facilityOrderTemplate;
        this.eligibilityStatus = componentsProps.eligibilityStatus;
        this.inactiveReason = inactiveReasons.find(i => i.id == facilityOrderTemplate.inactiveReasonId && i.id != 0);
        this.facilityOrderTemplateService = facilityOrderTemplateService;
        this.localDoctorService = new LocalDoctorService(rootStore.application.baasic.apiClient);
        this.diagnosisCodeService = new DiagnosisCodeService(rootStore.application.baasic.apiClient);

        this.facilityOrderTemplatePNMissingInformationModal = new ModalParams({});
        this.facilityOrderTemplateMissingDocumentNoteModal = new ModalParams({ onClose: () => this.form.submit() });

        this.doctorStore = new BaasicDropdownStore({
            textField: 'orderingDoctor',
            filterable: true,
            returnObject: true,
        }, {
            fetchFunc: async (term) => {
                if (!this.itemInitialized && this.facilityOrderTemplate.orderingDoctor && this.facilityOrderTemplate.orderingDoctor.npi) {
                    term = this.facilityOrderTemplate.orderingDoctor && this.facilityOrderTemplate.orderingDoctor.npi;
                }
                const response = await this.localDoctorService.find({ searchQuery: term, rpp: 20 });

                _.extend(response.data,
                    {
                        item: _.map(response.data.item, (item => {
                            return _.extend(
                                item, {
                                orderingDoctor: item.name + " - " + item.npi,
                            }
                            );
                        }))
                    }
                );

                return response.data.item;
            },
            onChange: async () => {
                await this.updateFacilityOrderTempalte("orderingDoctorId", true);
            }
        });

        this.missingDocumentStore = new BaasicDropdownStore({ multi: true, filterable: true }, {
            fetchFunc: async (term = "") => {
                return this.filterMissingDocumentStore(this.missingDocuments.filter(mc => (mc.itemCategoryId === this.facilityOrderTemplate.subCategory.itemCategoryId || !mc.itemCategoryId) && mc.name.toLowerCase().includes(term.toLowerCase())));
            },
            onChange: () => {
                this.form.submit();
            }
        });

        this.dxCode1Store = this.createDxCodeDropdownStore("dxCode1");
        this.dxCode2Store = this.createDxCodeDropdownStore("dxCode2");
        this.dxCode3Store = this.createDxCodeDropdownStore("dxCode3");
        this.dxCode4Store = this.createDxCodeDropdownStore("dxCode4");
        this.dxCode5Store = this.createDxCodeDropdownStore("dxCode5");
        this.dxCode6Store = this.createDxCodeDropdownStore("dxCode6");
        this.dxCode7Store = this.createDxCodeDropdownStore("dxCode7");
        this.dxCode8Store = this.createDxCodeDropdownStore("dxCode8");

        this.fetch([
            this.initialize()
        ]);
    }

    @action
    async initialize() {
        await this.getResource(this.id);
    }

    @action
    async setItem(item) {
        this.item = item;
        await this.setDefaultStoreValue(item);
        this.itemInitialized = true;
    }

    @action.bound
    createDxCodeDropdownStore(propertyName) {
        return new BaasicDropdownStore(
            { filterable: true, dataItemKey: "code", textField: "code" },
            {
                fetchFunc: async (term = this.facilityOrderTemplate[propertyName]) => {
                    if (!term || term.trim() === '') {
                        term = undefined;
                    }

                    if(!this.itemInitialized){
                       return [];
                    }

                    const response = await this.diagnosisCodeService.find({ searchQuery: term, rpp: 10, itemCategoryId: this.facilityOrderTemplate && this.facilityOrderTemplate.itemCategoryId, isBillable: true });
                    return response.data.item;
                },
                onChange: async () => {
                    await this.updateFacilityOrderTempalte("dxCode", true);
                }
            }
        );
    }
    
    @action.bound
    async setDefaultStoreValue(data) {
        this.dxCode1Store.setValue(data.dxCode1 && { code: data.dxCode1 });
        this.dxCode2Store.setValue(data.dxCode2 && { code: data.dxCode2 });
        this.dxCode3Store.setValue(data.dxCode3 && { code: data.dxCode3 });
        this.dxCode4Store.setValue(data.dxCode4 && { code: data.dxCode4 });
        this.dxCode5Store.setValue(data.dxCode5 && { code: data.dxCode5 });
        this.dxCode6Store.setValue(data.dxCode6 && { code: data.dxCode6 });
        this.dxCode7Store.setValue(data.dxCode7 && { code: data.dxCode7 });
        this.dxCode8Store.setValue(data.dxCode8 && { code: data.dxCode8 });
    }

    @action.bound
    async setIncludeInDocReqEmail(checked) {
        this.item.includeInDocReqEmail = checked;
        await this.facilityOrderTemplateService.update(this.item);
    }

    @action.bound
    filterMissingDocumentStore(missingDocuments) {
        let result = missingDocuments;
        if (missingDocuments && missingDocuments.some(m => m)) {
            let facilityOrderTemplateItems = this.facilityOrderTemplate && this.facilityOrderTemplate.facilityOrderTemplateItems;

            const deleteImportActionId = this.lookups.importAction.find(x => x.abrv === 'delete').id;

            const diabeticFormulaSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "diabetic-formula").id;
            const renalFormulaNeproNRSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "renal-formula-nepro-nr").id;
            const renalFormulaSuplenaSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "renal-formula-suplena").id;
            const pulmonaryFormulaSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "pulmonary-formula").id;
            const elementalFormulaSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "elemental-formula").id;
            const proteinSupplementSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "protein-supplement").id;
            const siliconeCatheterSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "silicone-catheter").id;
            const coudeCatheterSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "coude-catheter").id;

            if (!(facilityOrderTemplateItems && facilityOrderTemplateItems.some(i => i.importActionId != deleteImportActionId && i.product && i.product.specialtyTypeId == diabeticFormulaSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-diabetic");
            }
            if (!(facilityOrderTemplateItems && facilityOrderTemplateItems.some(i => i.importActionId != deleteImportActionId && i.product && i.product.specialtyTypeId == renalFormulaNeproNRSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-renal-nepro");
            }
            if (!(facilityOrderTemplateItems && facilityOrderTemplateItems.some(i => i.importActionId != deleteImportActionId && i.product && i.product.specialtyTypeId == renalFormulaSuplenaSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-renal-suplena");
            }
            if (!(facilityOrderTemplateItems && facilityOrderTemplateItems.some(i => i.importActionId != deleteImportActionId && i.product && i.product.specialtyTypeId == pulmonaryFormulaSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-pulmonary");
            }
            if (!(facilityOrderTemplateItems && facilityOrderTemplateItems.some(i => i.importActionId != deleteImportActionId && i.product && i.product.specialtyTypeId == elementalFormulaSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-elemental");
            }
            if (!(facilityOrderTemplateItems && facilityOrderTemplateItems.some(i => i.importActionId != deleteImportActionId && i.product && i.product.specialtyTypeId == proteinSupplementSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-protein-supplement");
            }
            if (!(facilityOrderTemplateItems && facilityOrderTemplateItems.some(i => i.importActionId != deleteImportActionId && i.product && i.product.specialtyTypeId == siliconeCatheterSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-silicone-catheter");
            }
            if (!(facilityOrderTemplateItems && facilityOrderTemplateItems.some(i => i.importActionId != deleteImportActionId && i.product && i.product.specialtyTypeId == coudeCatheterSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-coude-catheter");
            }
        }
        return result;
    }

    @action.bound
    openFacilityOrderTemplateMissingDocumentNoteModal(missingDocument) {
        if (this.facilityOrderTemplate.facilityOrderTemplateMissingDocuments.find(x => x.missingDocument.id == missingDocument.id)) {
            this.facilityOrderTemplateMissingDocumentNoteModal.open({
                missingDocument: _.extend(missingDocument, { facilityOrderTemplateId: this.facilityOrderTemplate.id, facilityOrderTemplateMissingDocumentId: this.facilityOrderTemplate.facilityOrderTemplateMissingDocuments.find(x => x.missingDocument.id == missingDocument.id).id })
            });
        }
    }

    @action.bound
    async updateResource(resource) {
        if (!this.actions.update) return;

        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            var response = await this.actions.update({
                id: this.id,
                isPartialUpdate: true,
                includeInDocReqEmail: this.item.includeInDocReqEmail,
                ...resource
            });
            
            this.facilityOrderTemplate.note = response.note;
            this.facilityOrderTemplate.swo = response.swo;
            this.facilityOrderTemplate.dxCode1 = response.dxCode1;
            this.facilityOrderTemplate.dxCode2 = response.dxCode2;
            this.facilityOrderTemplate.dxCode3 = response.dxCode3;
            this.facilityOrderTemplate.dxCode4 = response.dxCode4;
            this.facilityOrderTemplate.dxCode5 = response.dxCode5;
            this.facilityOrderTemplate.dxCode6 = response.dxCode6;
            this.facilityOrderTemplate.dxCode7 = response.dxCode7;
            this.facilityOrderTemplate.dxCode8 = response.dxCode8;
            this.facilityOrderTemplate.facilityOrderTemplateMissingDocuments = response.facilityOrderTemplateMissingDocuments;
            this.facilityOrderTemplate.orderingDoctor = response.orderingDoctor;
            this.facilityOrderTemplate.orderingDoctorId = response.orderingDoctorId;
            this.facilityOrderTemplate.clinicalStatusId = response.clinicalStatusId;
            this.facilityOrderTemplate.clinicalStatusNote = response.clinicalStatusNote;
            this.facilityOrderTemplate.clinicalStatus = response.clinicalStatus;

            this.form.setFieldsDisabled(false);
        }
        catch (err) {
            this.form.setFieldsDisabled(false);
            return this.onUpdateError(err);
        }
    }

    @action.bound
    changeItemVisibility() {
        this.isItemVisible = !this.isItemVisible;
    }

    @action.bound
    async updateFacilityOrderTempalte(propertyName, submit = false) {
        if (submit || this.facilityOrderTemplate[propertyName] != this.form.$(propertyName).value) {
            this.form.submit();
        }
    }
    
    @action.bound
    async updatePatient() {
        if (!this.actions.updatePatientDetails) return;
        this.form.setFieldsDisabled(true);
        try {
            let resource = { height: this.form.$("height").value, weight: this.form.$("weight").value }
            if (resource.height != this.facilityOrderTemplate.patient.height || resource.weight != this.facilityOrderTemplate.patient.weight) {
                await this.actions.updatePatientDetails({
                    patientBrightreeId: this.facilityOrderTemplate.patient.patientKey,
                    height: resource.height,
                    weight: resource.weight,
                    ssn: this.facilityOrderTemplate.patient.ssn
                });

                this.facilityOrderTemplate.patient.height = resource.height;
                this.facilityOrderTemplate.patient.weight = resource.weight;
            }
            this.form.setFieldsDisabled(false);
            return;
        }
        catch (err) {
            this.form.setFieldsDisabled(false);
            return this.onUpdateError(err);
        }
    }

    getMissingInformationText() {
        let isPatientWeightMissing = this.item.patient && !this.item.patient.weight;
        let isPatientHeightMissing = this.item.patient && !this.item.patient.height;
        let isCalorieAmountMissing = this.item.facilityOrderTemplateItems.some(x => x.product && x.product.itemTypeId && !x.itemCals && x.importActionId != this.lookups.importAction.find(y => y.abrv == 'delete').id);
        let isPatientGenderMissing =  this.item.patient && ! this.item.patient.gender;
        let isMlphMissing = this.item.facilityOrderTemplateItems.some(x => x.product && x.product.itemTypeId && !x.mlph && x.importActionId != this.lookups.importAction.find(y => y.abrv == 'delete').id);

        let missingItems = [];
        if (isPatientWeightMissing) {
            missingItems.push("weight");
        }
        if (isPatientHeightMissing) {
            missingItems.push("height");
        }
        if (isCalorieAmountMissing) {
            missingItems.push("total calories");
        }
        if (isPatientGenderMissing) {
            missingItems.push("gender");
        }
        if (isMlphMissing) {
            missingItems.push("flow rate");
        }
        missingItems.sort();

        if (missingItems.length > 0)
            return missingItems;
        return null;
    }

    getMissingInformationUrological() {
        return this.item.facilityOrderTemplateItems.some(x =>
            (
                (x.product && x.product.procedureCode_ && (x.product.procedureCode_.abrv === "A4352")) ||
                (x.product && x.product.procedureCode_ && (x.product.procedureCode_.abrv === "A4353")) ||
                (x.product && x.product.procedureCode_ && (x.product.procedureCode_.abrv === "A4351"))
            ) &&
            ((!x.templateQuantity) || (x.templateQuantity == 0) || (x.templateQuantity % 28 != 0))
        );
    }

    @action.bound
    async generateMedicalNecessityForm() {
        try {
            if (this.item.subCategory.itemCategory.abrv === "enteral" && this.getMissingInformationText()) {
                this.facilityOrderTemplatePNMissingInformationModal.open({
                    missingInformation: this.getMissingInformationText(),
                    generateMedicalNecessityForm: () => this.openMedicalNecessityWindow()
                });
            }
            else if (this.item.subCategory.itemCategory.abrv === "urological" && this.getMissingInformationUrological()) {
                this.facilityOrderTemplatePNMissingInformationModal.open({
                    missingInformation: ["Qty per day (system cannot determine)"],
                    generateMedicalNecessityForm: () => this.openMedicalNecessityWindow()
                });
            }
            else {
                this.openMedicalNecessityWindow();
            }
        }
        catch (err) {
            this.rootStore.notificationStore.error("Error occurred while trying to generate Medical Necessity Form.");
        }
    }
    
    openMedicalNecessityWindow() {
        var url = `${this.facilityOrderTemplateService.getMedicalNecessityFormReportUrl(this.facilityOrderTemplate.id)}?contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
        window.open(url, "_blank")
        this.rootStore.notificationStore.success("Progress Notes document successfully generated.");
    }

    @action.bound
    async generateStandardWrittenOrder() {
        try {
            var url = `${this.facilityOrderTemplateService.getStandardWrittenOrderReportUrl(this.item.id)}?contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
            window.open(url, "_blank")
            this.rootStore.notificationStore.success("Standard Written Order successfully generated.");
        }
        catch (err) {
            this.rootStore.notificationStore.error("Error occurred while trying to generate Standard Written Order.");
        }
    }

    @action.bound
    async generateMedicalNecessityFormAndStandardWrittenOrder() {
        await this.generateMedicalNecessityForm();
        await this.generateStandardWrittenOrder();
    }
}

export default PatientMissingDocsTemplateEditViewStore;
